import type { AuthSession } from '@domain/models/auth.model';
import type { PageQuery, PagedResult } from '@domain/models/paged-result.model';
import type {
  SignupAttempt,
  User,
  UserTag,
  UserTransfer,
  UserWallet,
} from '@domain/models/user.model';

import type {
  ApiPagedResponse,
  AuthSessionDto,
  EmployeeSignInResponseDto,
  SignupAttemptDto,
  UserDto,
  UserTagDto,
  UserTransferDto,
  UserWalletDto,
} from '@data/dto/api.dto';

export function mapUserDto(dto: UserDto): User {
  return {
    id: dto.userId,
    name: dto.name ?? '',
    email: dto.email,
    phoneNumber: dto.phoneNumber,
    pin: dto.pin,
    ageGroup: dto.ageGroup,
    gender: dto.gender,
    language: dto.language,
    avatarUrl: dto.avatarUrl,
    userTagName: dto.userTagName ?? '',
    storeCount: dto.isHasStore ?? 0,
    lastActive: dto.lastActive ? new Date(dto.lastActive) : null,
    createdOn: new Date(dto.createdOn),
    address: dto.address,
    latitude: dto.latitude,
    longitude: dto.longitude,
    reasonCancel: dto.reasonCancel,
    isDownMembership: dto.isDownMembership,
    isSubscription: dto.isSubscription ?? null,
    typeSubscription: dto.typeSubscription ?? null,
    amountSubscription: dto.amountSubscription ?? null,
    remainedPaymentDays: dto.remainedPaymentDays ?? null,
    paymentDateExpired: dto.paymentDateExpired ?? null,
    numberOfStore: dto.numberOfStore ?? null,
  };
}

export function mapPagedUsers(response: ApiPagedResponse<UserDto>): PagedResult<User> {
  return {
    items: (response.items ?? []).map(mapUserDto),
    total: response.total ?? 0,
    page: response.page ?? 1,
    pageSize: response.limit ?? 20,
  };
}

export function mapUserWalletDto(dto: UserWalletDto): UserWallet {
  return {
    totalBalance: dto.totalBalance,
    endingDayBalance: dto.endingDayBalance,
    endingWeekBalance: dto.endingWeekBalance,
    totalDailyStepBalance: dto.totalDailyStepBalance,
    totalTransferBalance: dto.totalTransferBalance ?? dto.totalTranferBalance ?? 0,
    adminCreditBalance: dto.adminCreditBalance,
    negativeBalance: dto.negativeBalance,
  };
}

export function mapUserTransferDto(dto: UserTransferDto): UserTransfer {
  return {
    transferId: dto.transferId,
    senderUserId: dto.senderUserId,
    recipientUserId: dto.recipientUserId,
    amount: dto.amount,
    purpose: dto.purpose,
    expiry: new Date(dto.expiry),
    isGift: dto.isGift,
    senderName: dto.senderName,
    senderPhoneNumber: dto.senderPhoneNumber,
    recipientName: dto.recipientName,
    recipientPhoneNumber: dto.recipientPhoneNumber,
    transferDate: new Date(dto.transferDate),
  };
}

export function mapPagedTransfers(
  response: ApiPagedResponse<UserTransferDto>,
): PagedResult<UserTransfer> {
  return {
    items: (response.items ?? []).map(mapUserTransferDto),
    total: response.total ?? 0,
    page: response.page ?? 1,
    pageSize: response.limit ?? 20,
  };
}

export function mapSignupAttemptDto(dto: SignupAttemptDto): SignupAttempt {
  return {
    phoneNumber: dto.phoneNumber,
    pin: dto.pin,
    ageGroup: dto.ageGroup,
    gender: dto.gender,
    deviceType: dto.deviceType,
    deviceId: dto.deviceId,
    createdOn: dto.createdOn,
  };
}

export function mapPagedSignupAttempts(
  response: ApiPagedResponse<SignupAttemptDto>,
): PagedResult<SignupAttempt> {
  return {
    items: (response.items ?? []).map(mapSignupAttemptDto),
    total: response.total ?? 0,
    page: response.page ?? 1,
    pageSize: response.limit ?? 20,
  };
}

export function mapUserTagDto(dto: UserTagDto): UserTag {
  return {
    id: dto.userTagId,
    name: dto.name,
    isDefault: dto.isDefault,
  };
}

export function mapPagedUserTags(response: ApiPagedResponse<UserTagDto>): PagedResult<UserTag> {
  return {
    items: (response.items ?? []).map(mapUserTagDto),
    total: response.total ?? 0,
    page: response.page ?? 1,
    pageSize: response.limit ?? 20,
  };
}

export function mapAuthSessionDto(dto: AuthSessionDto): AuthSession {
  return {
    accessToken: dto.accessToken,
    refreshToken: dto.refreshToken,
    expiresAt: new Date(dto.expiresAt),
    user: {
      id: dto.user.id,
      fullName: dto.user.fullName,
      email: dto.user.email,
      roles: dto.user.roles,
    },
  };
}

/**
 * Maps POST api/Employees/sign-in → AuthSession.
 * User profile fields are read from JWT claims (UserId, Name, Email, role).
 */
export function mapEmployeeSignInResponse(
  dto: EmployeeSignInResponseDto,
  fallbackEmail: string,
): AuthSession {
  const claims = decodeJwtPayload(dto.accessToken);
  const roleClaim =
    claims['role'] ??
    claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];

  const roles = Array.isArray(roleClaim)
    ? roleClaim.map(String)
    : roleClaim
      ? [String(roleClaim)]
      : ['Admin'];

  const exp = typeof claims['exp'] === 'number' ? claims['exp'] : null;

  return {
    accessToken: dto.accessToken,
    refreshToken: null,
    expiresAt: exp
      ? new Date(exp * 1000)
      : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    user: {
      id: String(claims['UserId'] ?? ''),
      fullName: String(claims['Name'] ?? fallbackEmail),
      email: String(claims['Email'] ?? fallbackEmail),
      roles,
    },
  };
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const parts = token.split('.');
    if (parts.length < 2) {
      return {};
    }

    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/** API uses page + limit (not pageSize). */
export function toPageParams(query: PageQuery): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {
    page: query.page,
    limit: query.pageSize,
  };

  if (query.search) params['search'] = query.search;
  if (query.sortBy) params['sortBy'] = query.sortBy;
  if (query.sortDirection) params['sortDirection'] = query.sortDirection;

  return params;
}
